import React, { useState, useEffect, useCallback } from 'react';
import Dropdown from '../../Components/utils/ReuseDropdown';
import axios from 'axios';
import { showToast } from '../../Components/utils/ShowToast';
import { MdKeyboardArrowRight } from 'react-icons/md';
import { FaLock, FaLockOpen, FaFileAlt, FaCheck, FaTimes, FaSyncAlt, FaBuilding, FaUser, FaEnvelope, FaMapMarkerAlt, FaIndustry, FaGlobe, FaCalendarAlt, FaBoxes, FaWarehouse, FaShippingFast, FaTags, FaCertificate } from 'react-icons/fa';
import { MdChat, MdBusiness } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import Loader from '../../Components/utils/Loader';
import { formatFullAddress } from '../../Components/utils/formatAddress';
import ConfirmDialog from '../../Components/utils/ReuseConfirmDialog';
import { IoIosCloseCircleOutline } from 'react-icons/io';

function AdminSellers() {
    const [sellers, setSellers] = useState([]);
    const [filterType, setFilterType] = useState('All');
    const [isLoading, setIsLoading] = useState(true);
    const [isBlockingLoading, setIsBlockingLoading] = useState(false);
    const [isActionLoading, setIsActionLoading] = useState(false);

    const [applications, setApplications] = useState([]);
    const [applicationsStatusFilter, setApplicationsStatusFilter] = useState('pending');
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [rejectModal, setRejectModal] = useState({ open: false, applicationId: null, isBulk: false });
    const [rejectReason, setRejectReason] = useState('');

    const [b2bSettings, setB2bSettings] = useState({ sellerApprovalRequired: true });
    const [settingsLoading, setSettingsLoading] = useState(true);

    const [selectedApplication, setSelectedApplication] = useState(null);

    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        title: '',
        message: '',
        onConfirm: null,
    });

    const navigate = useNavigate();

    const pendingApplications = applications.filter((item) => item.status === 'pending');

    const fetchSellers = useCallback(async () => {
        setIsLoading(true);
        const token = localStorage.getItem('adminToken');
        try {
            const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/admins/sellers/all/`, {
                headers: { Authorization: `Bearer ${token}` },
                params: { filterType },
            });
            if (response.data.success) {
                const sortedSellers = response.data.allSellers.sort(
                    (a, b) => new Date(b.userId.updatedAt) - new Date(a.userId.updatedAt)
                );
                setSellers(sortedSellers);
            }
        } catch (e) {
            showToast(e.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsLoading(false);
        }
    }, [filterType]);

    const fetchApplications = useCallback(async () => {
        setApplicationsLoading(true);
        const token = localStorage.getItem('adminToken');
        try {
            const params = {};
            if (applicationsStatusFilter !== 'all') params.status = applicationsStatusFilter;
            const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/applications`, {
                headers: { Authorization: `Bearer ${token}` },
                params,
            });
            setApplications(response.data.applications || []);
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to fetch business seller requests', 'error');
        } finally {
            setApplicationsLoading(false);
        }
    }, [applicationsStatusFilter]);

    const fetchB2BSettings = useCallback(async () => {
        setSettingsLoading(true);
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/b2b-config`,
                { params: { _t: Date.now() } }
            );
            setB2bSettings(response.data?.b2b || { sellerApprovalRequired: true });
        } catch (e) {
            showToast('Failed to load B2B review settings', 'error');
        } finally {
            setSettingsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSellers();
    }, [fetchSellers]);

    useEffect(() => {
        fetchApplications();
    }, [fetchApplications]);

    useEffect(() => {
        fetchB2BSettings();
    }, [fetchB2BSettings]);

    const openConfirmDialog = (title, message, onConfirm) => {
        setConfirmDialog({ open: true, title, message, onConfirm });
    };

    const closeConfirmDialog = () => {
        setConfirmDialog({ open: false, title: '', message: '', onConfirm: null });
    };

    const handleConfirm = async () => {
        if (!confirmDialog.onConfirm) return;
        await confirmDialog.onConfirm();
        closeConfirmDialog();
    };

    const handleBlockUser = async (userId, isBlocked) => {
        setIsBlockingLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/block/`,
                { userId, isBlocked },
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data.success) {
                showToast(isBlocked ? 'User has been unblocked!' : 'User has been blocked!', 'success');
                fetchSellers();
            }
        } catch (e) {
            showToast(e.response?.data?.error || 'Something went wrong!', 'error');
        } finally {
            setIsBlockingLoading(false);
        }
    };

    const updateReviewSetting = async (enabled) => {
        setSettingsLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.post(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/settings/admin/update/b2b`,
                { b2b: { sellerApprovalRequired: enabled } },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            await fetchB2BSettings();
            showToast(enabled ? 'Business account review enabled' : 'Business account auto-approval enabled', 'success');
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to update review setting', 'error');
        } finally {
            setSettingsLoading(false);
        }
    };

    const approveApplication = async (applicationId) => {
        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/applications/${applicationId}/approve`,
                {},
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showToast('Business seller request approved', 'success');
            fetchApplications();
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to approve request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const declineApplication = async (applicationId, reason = '') => {
        setIsActionLoading(true);
        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(
                `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/applications/${applicationId}/decline`,
                { reason },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            showToast('Business seller request rejected', 'success');
            fetchApplications();
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to reject request', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const approveAllPending = async () => {
        if (!pendingApplications.length) {
            showToast('No pending requests to approve', 'info');
            return;
        }

        const token = localStorage.getItem('adminToken');
        setIsActionLoading(true);

        try {
            await Promise.all(
                pendingApplications.map((app) =>
                    axios.put(
                        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/applications/${app._id}/approve`,
                        {},
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                )
            );
            showToast('All pending business seller requests approved', 'success');
            fetchApplications();
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to approve all pending requests', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const rejectAllPending = async (reason = '') => {
        if (!pendingApplications.length) {
            showToast('No pending requests to reject', 'info');
            return;
        }

        const token = localStorage.getItem('adminToken');
        setIsActionLoading(true);

        try {
            await Promise.all(
                pendingApplications.map((app) =>
                    axios.put(
                        `${process.env.REACT_APP_Backend_Host_Name}/api/v1/b2b/admin/applications/${app._id}/decline`,
                        { reason },
                        { headers: { Authorization: `Bearer ${token}` } }
                    )
                )
            );
            showToast('All pending business seller requests rejected', 'success');
            fetchApplications();
        } catch (e) {
            showToast(e.response?.data?.error || 'Failed to reject all pending requests', 'error');
        } finally {
            setIsActionLoading(false);
        }
    };

    const openRejectModal = (applicationId, isBulk = false) => {
        setRejectReason('');
        setRejectModal({ open: true, applicationId, isBulk });
    };

    const closeRejectModal = () => {
        setRejectModal({ open: false, applicationId: null, isBulk: false });
        setRejectReason('');
    };

    const handleConfirmReject = async () => {
        if (rejectModal.isBulk) {
            await rejectAllPending(rejectReason);
        } else {
            await declineApplication(rejectModal.applicationId, rejectReason);
        }
        closeRejectModal();
    };

    const sellerElems = sellers.length > 0 ? (
        sellers.map((item, index) => (
            <div key={item._id || index}>
                <div className='requestRow row'>
                    <div className='titleField field'>
                        <p className='title'>{item.userId?.email}</p>
                    </div>
                    <p className='usernameField field'>{item.userId?.username}</p>
                    <p className='typeField field'>{item.sellerType}</p>
                    <p className='statusField field'>{item?.planType}</p>
                    <p className='joinField field'>{new Date(item?.createdAt).toLocaleDateString()}</p>
                    <div className='actionsField field'>
                        <MdChat
                            className='icon'
                            onClick={() => navigate(`/chwi-admin/chats/?p=${item.userId?._id}`)}
                        />
                        {item.userId.userStatus === 'Blocked' ? (
                            <FaLockOpen
                                style={{ color: 'var(--success)' }}
                                className='icon'
                                onClick={() =>
                                    openConfirmDialog(
                                        'Unblock User',
                                        'Are you sure you want to unblock this user?',
                                        () => handleBlockUser(item.userId._id, true)
                                    )
                                }
                            />
                        ) : (
                            <FaLock
                                style={{ color: 'var(--danger)' }}
                                className='icon'
                                onClick={() =>
                                    openConfirmDialog(
                                        'Block User',
                                        'Are you sure you want to block this user?',
                                        () => handleBlockUser(item.userId._id, false)
                                    )
                                }
                            />
                        )}
                        <MdKeyboardArrowRight
                            className='icon arrowRight'
                            onClick={() => navigate(`/vendorDetail/${item._id}`)}
                        />
                    </div>
                </div>
                {sellers.length > 1 && sellers.length - 1 !== index && <div className='horizontalLine'></div>}
            </div>
        ))
    ) : (
        <div className='row'>Nothing to show here...</div>
    );

    return (
        <div className='adminSellersDiv'>
            <div className='adminSellersContent'>
                <div className='adminBusinessRequestsPanel'>
                    <div className='adminBusinessRequestsHeader'>
                        <div>
                            <h2 className='secondaryHeading'>Business Seller Review Queue</h2>
                            <p className='adminBusinessSubText'>Review pending business seller requests, approve/reject quickly, and manage review settings.</p>
                        </div>
                        <button
                            type='button'
                            className='secondaryBtn adminBusinessRefreshBtn'
                            onClick={fetchApplications}
                        >
                            <FaSyncAlt /> Refresh
                        </button>
                    </div>

                    <div className='adminBusinessReviewToggle'>
                        <div>
                            <h4>Require Review For New Business Accounts</h4>
                            <p>
                                {b2bSettings.sellerApprovalRequired
                                    ? 'Enabled: business seller requests stay pending until admin approval.'
                                    : 'Disabled: new business requests are approved automatically.'}
                            </p>
                        </div>
                        <label className='adminBusinessSwitch'>
                            <input
                                type='checkbox'
                                checked={Boolean(b2bSettings.sellerApprovalRequired)}
                                onChange={(e) => {
                                    const nextEnabled = e.target.checked;
                                    openConfirmDialog(
                                        nextEnabled ? 'Enable Business Review?' : 'Disable Business Review?',
                                        nextEnabled
                                            ? 'New business accounts will be held for review before B2B selling is enabled.'
                                            : 'New business accounts will be approved automatically for B2B eligibility.',
                                        () => updateReviewSetting(nextEnabled)
                                    );
                                }}
                                disabled={settingsLoading}
                            />
                            <span className='adminBusinessSlider'></span>
                        </label>
                    </div>

                    <div className='adminBusinessActionsRow'>
                        <div className='adminBusinessFilters'>
                            <Dropdown
                                options={['pending', 'approved', 'declined', 'all']}
                                onSelect={setApplicationsStatusFilter}
                                selected={applicationsStatusFilter}
                            />
                            <span className='adminBusinessCount'>
                                {applicationsLoading ? 'Loading...' : `${applications.length} requests`}
                            </span>
                        </div>

                        <div className='adminBusinessBulkActions'>
                            <button
                                className='secondaryBtn adminBusinessBulkBtn'
                                disabled={!pendingApplications.length || applicationsLoading}
                                onClick={() =>
                                    openConfirmDialog(
                                        'Approve All Pending Requests?',
                                        `This will approve ${pendingApplications.length} pending business seller requests.`,
                                        approveAllPending
                                    )
                                }
                            >
                                <FaCheck /> Approve All Pending
                            </button>
                            <button
                                className='dangerBtn adminBusinessBulkBtn'
                                disabled={!pendingApplications.length || applicationsLoading}
                                onClick={() => openRejectModal(null, true)}
                            >
                                <FaTimes /> Reject All Pending
                            </button>
                        </div>
                    </div>

                    {applicationsLoading ? (
                        <Loader type='simpleMini' />
                    ) : applications.length === 0 ? (
                        <div className='adminBusinessEmpty'>No business seller requests in this filter.</div>
                    ) : (
                        <div className='adminBusinessRequestsTableWrap'>
                            <table className='adminBusinessRequestsTable'>
                                <thead>
                                    <tr>
                                        <th>Company</th>
                                        <th>Seller</th>
                                        <th>Location</th>
                                        <th>Organization</th>
                                        <th>Status</th>
                                        <th>Submitted</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applications.map((app) => (
                                        <tr key={app._id}>
                                            <td>{app.companyName}</td>
                                            <td>
                                                <div className='adminBusinessSellerCell'>
                                                    <span>{app.userId?.username || 'N/A'}</span>
                                                    <small>{app.userId?.email || 'N/A'}</small>
                                                </div>
                                            </td>
                                            <td>{formatFullAddress(app.userId?.buyerAddress) || '-'}</td>
                                            <td>{app.natureOfOrg || '-'}</td>
                                            <td>
                                                <span className={`adminBusinessStatus status-${app.status}`}>{app.status}</span>
                                            </td>
                                            <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                                            <td>
                                                <div className='adminBusinessRowActions'>
                                                    <button
                                                        type='button'
                                                        className='adminBusinessActionBtn adminBusinessActionBtn--details'
                                                        onClick={() => setSelectedApplication(app)}
                                                    >
                                                        <FaFileAlt /> Details
                                                    </button>
                                                    {app.status === 'pending' && (
                                                        <>
                                                            <button
                                                                type='button'
                                                                className='adminBusinessActionBtn adminBusinessActionBtn--approve'
                                                                onClick={() =>
                                                                    openConfirmDialog(
                                                                        'Approve Business Request?',
                                                                        `Approve business seller request for ${app.companyName}?`,
                                                                        () => approveApplication(app._id)
                                                                    )
                                                                }
                                                            >
                                                                <FaCheck /> Approve
                                                            </button>
                                                            <button
                                                                type='button'
                                                                className='adminBusinessActionBtn adminBusinessActionBtn--reject'
                                                                onClick={() => openRejectModal(app._id, false)}
                                                            >
                                                                <FaTimes /> Reject
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                <div className='tableDiv'>
                    <div className='tableContent'>
                        <div className='upper'>
                            <h2 className='secondaryHeading'>
                                <span>{filterType} </span>Sellers
                                <span className='totalRows'>- {(sellers.length < 10 && '0') + sellers.length}</span>
                            </h2>
                            <div className='upperRight'>
                                <Dropdown
                                    options={['All', 'Active', 'Blocked', 'Paid', 'Free']}
                                    onSelect={setFilterType}
                                    selected={filterType}
                                />
                            </div>
                        </div>
                        <div className='header'>
                            <p className='title'>Email</p>
                            <p>Username</p>
                            <p>Seller Type</p>
                            <p>Seller Plan</p>
                            <p>Seller Since</p>
                            <p>Actions</p>
                        </div>
                        {isLoading ? <Loader type='simpleMini' /> : <div className='rows'>{sellerElems}</div>}
                    </div>
                </div>
            </div>

            {selectedApplication && (
                <div className='popupDiv addNewModelDiv'>
                    <div className='popupContent'>
                        <div className='form bizDetailsForm'>
                            <div className='bizDetailsHeader'>
                                <div className='bizDetailsHeaderLeft'>
                                    <div className='bizDetailsIcon'><MdBusiness /></div>
                                    <div>
                                        <h2 className='secondaryHeading' style={{ margin: 0 }}>
                                            {selectedApplication.companyName || 'Business Request'}
                                        </h2>
                                        <p className='bizDetailsSubmitted'>
                                            Submitted {new Date(selectedApplication.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                        </p>
                                    </div>
                                </div>
                                <span className={`adminBusinessStatus status-${selectedApplication.status}`}>
                                    {selectedApplication.status}
                                </span>
                            </div>

                            <div className='bizDetailsGrid'>
                                <div className='bizDetailsSection'>
                                    <h4 className='bizDetailsSectionTitle'><FaBuilding /> Company Info</h4>
                                    <div className='bizDetailsRows'>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'>Company Name</span><span className='bizDetailsValue'>{selectedApplication.companyName || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaIndustry /> Organization</span><span className='bizDetailsValue'>{selectedApplication.natureOfOrg || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaCalendarAlt /> Founded</span><span className='bizDetailsValue'>{selectedApplication.yearFounded || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaUser /> Employees</span><span className='bizDetailsValue'>{selectedApplication.numberOfEmployees || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaGlobe /> Website</span><span className='bizDetailsValue'>{selectedApplication.website ? <a href={selectedApplication.website} target='_blank' rel='noreferrer'>{selectedApplication.website}</a> : '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaEnvelope /> Business Email</span><span className='bizDetailsValue'>{selectedApplication.businessEmail || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaMapMarkerAlt /> Business Address</span><span className='bizDetailsValue'>{formatFullAddress(selectedApplication.businessAddress) || '-'}</span></div>
                                    </div>
                                </div>

                                <div className='bizDetailsSection'>
                                    <h4 className='bizDetailsSectionTitle'><FaUser /> Seller Info</h4>
                                    <div className='bizDetailsRows'>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'>Username</span><span className='bizDetailsValue'>{selectedApplication.userId?.username || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaEnvelope /> Account Email</span><span className='bizDetailsValue'>{selectedApplication.userId?.email || '-'}</span></div>
                                        <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaMapMarkerAlt /> Location</span><span className='bizDetailsValue'>{formatFullAddress(selectedApplication.userId?.buyerAddress) || '-'}</span></div>
                                    </div>
                                </div>
                            </div>

                            <div className='bizDetailsSection bizDetailsSectionFull'>
                                <h4 className='bizDetailsSectionTitle'><FaBoxes /> Capacity & Operations</h4>
                                <div className='bizDetailsRows bizDetailsRowsHoriz'>
                                    <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaBoxes /> Production Capacity</span><span className='bizDetailsValue'>{selectedApplication.productionCapacity || '-'}</span></div>
                                    <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaWarehouse /> Inventory Capacity</span><span className='bizDetailsValue'>{selectedApplication.inventoryCapacity || '-'}</span></div>
                                    <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaShippingFast /> Export Markets</span><span className='bizDetailsValue'>{(selectedApplication.exportMarkets || []).join(', ') || '-'}</span></div>
                                    <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaTags /> Product Categories</span><span className='bizDetailsValue'>{(selectedApplication.productCategories || []).join(', ') || '-'}</span></div>
                                    <div className='bizDetailsRow'><span className='bizDetailsLabel'><FaCertificate /> Certifications</span><span className='bizDetailsValue'>{(selectedApplication.certifications || []).map((c) => c?.name).filter(Boolean).join(', ') || '-'}</span></div>
                                </div>
                            </div>

                            {selectedApplication.sellerId?.description && (
                                <div className='bizDetailsSection bizDetailsSectionFull'>
                                    <h4 className='bizDetailsSectionTitle'><FaFileAlt /> Business Description</h4>
                                    <p className='bizDetailsText'>{selectedApplication.sellerId.description}</p>
                                </div>
                            )}

                            {selectedApplication.adminNote && (
                                <div className='bizDetailsSection bizDetailsSectionFull bizDetailsAdminNote'>
                                    <h4 className='bizDetailsSectionTitle'>Admin Note</h4>
                                    <p className='bizDetailsText'>{selectedApplication.adminNote}</p>
                                </div>
                            )}

                            <div className='buttonsDiv'>
                                <button className='secondaryBtn' type='button' onClick={() => setSelectedApplication(null)}>
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='popupCloseBtn'>
                        <IoIosCloseCircleOutline className='icon' onClick={() => setSelectedApplication(null)} />
                    </div>
                </div>
            )}

            {rejectModal.open && (
                <div className='popupDiv addNewModelDiv'>
                    <div className='popupContent'>
                        <div className='form'>
                            <h2 className='secondaryHeading'>
                                {rejectModal.isBulk ? 'Reject All Pending' : 'Reject Business Request'}
                            </h2>
                            <p className='adminBusinessSubText' style={{ marginBottom: '16px' }}>
                                {rejectModal.isBulk
                                    ? `This will reject ${pendingApplications.length} pending business seller request${pendingApplications.length !== 1 ? 's' : ''}.`
                                    : 'You are about to reject this business seller request.'}
                            </p>
                            <div className='rejectModalField'>
                                <label className='rejectModalLabel'>
                                    Rejection Reason <span className='rejectModalOptional'>(optional)</span>
                                </label>
                                <textarea
                                    className='inputField rejectModalTextarea'
                                    placeholder='e.g. Incomplete documentation, insufficient production capacity...'
                                    value={rejectReason}
                                    onChange={(e) => setRejectReason(e.target.value)}
                                    rows={4}
                                />
                                <p className='rejectModalNote'>
                                    If a reason is provided, the seller will be notified via a Chocowi notification and will be able to see it.
                                </p>
                            </div>
                            <div className='buttonsDiv'>
                                <button className='secondaryBtn' type='button' onClick={closeRejectModal} disabled={isActionLoading}>
                                    Cancel
                                </button>
                                <button className='dangerBtn' type='button' onClick={handleConfirmReject} disabled={isActionLoading}>
                                    {isActionLoading ? 'Rejecting...' : 'Confirm Reject'}
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className='popupCloseBtn'>
                        <MdKeyboardArrowRight className='icon' onClick={closeRejectModal} />
                    </div>
                </div>
            )}

            <ConfirmDialog
                open={confirmDialog.open}
                title={confirmDialog.title}
                message={confirmDialog.message}
                onConfirm={handleConfirm}
                onCancel={closeConfirmDialog}
                isLoading={isBlockingLoading || settingsLoading || isActionLoading}
            />
        </div>
    );
}

export default AdminSellers;
